// ===============================================================================
// balanceUtils.js - Balance Calculation Utilities for SplitMon Frontend
// ===============================================================================
// Location: /var/www/wow/frontend/src/utils/balanceUtils.js
//
// PURPOSE:
// This module provides client-side balance calculation utilities, including
// the simplifyDebts algorithm. By running this on the frontend, we:
// 1. Reduce server CPU load (distributed across 1000s of browsers)
// 2. Provide instant UI updates without server round-trips
// 3. Enable offline calculation capability
//
// USAGE:
// import { simplifyDebts, formatBalance } from './utils/balanceUtils';
// const debts = simplifyDebts(balances);
//
// ===============================================================================

/**
 * Threshold for considering a balance as "settled" (handles floating point issues)
 */
const BALANCE_THRESHOLD = 0.01;

/**
 * Simplify debts to minimize the number of transactions needed.
 * 
 * Algorithm:
 * 1. Separate users into creditors (positive balance) and debtors (negative balance)
 * 2. Sort both by absolute amount (descending)
 * 3. Match largest debtor with largest creditor
 * 4. Transfer the minimum of the two amounts
 * 5. Repeat until all settled
 * 
 * This greedy algorithm minimizes the number of transactions while ensuring
 * everyone gets/pays the correct amount.
 * 
 * @param {Array} balances - Array of balance objects from API
 *   Each balance: { user_id, username, name, net_balance, ... }
 *   - Positive net_balance = others owe this user (creditor)
 *   - Negative net_balance = this user owes others (debtor)
 * 
 * @returns {Array} Array of simplified debt objects
 *   Each debt: { from_user_id, from_user_name, to_user_id, to_user_name, amount }
 * 
 * @example
 * const balances = [
 *   { user_id: 1, name: "Alice", net_balance: 50 },   // Owed €50
 *   { user_id: 2, name: "Bob", net_balance: -30 },    // Owes €30
 *   { user_id: 3, name: "Carol", net_balance: -20 }, // Owes €20
 * ];
 * 
 * const debts = simplifyDebts(balances);
 * // Returns:
 * // [
 * //   { from_user_id: 2, from_user_name: "Bob", to_user_id: 1, to_user_name: "Alice", amount: 30 },
 * //   { from_user_id: 3, from_user_name: "Carol", to_user_id: 1, to_user_name: "Alice", amount: 20 }
 * // ]
 */
export function simplifyDebts(balances) {
  if (!balances || !Array.isArray(balances) || balances.length === 0) {
    return [];
  }

  // Build user info lookup for names
  const userInfo = {};
  balances.forEach(b => {
    userInfo[b.user_id] = {
      name: b.name || b.username || `User ${b.user_id}`,
      username: b.username || ''
    };
  });

  // Separate into creditors (positive balance) and debtors (negative balance)
  const creditors = []; // People who are owed money
  const debtors = [];   // People who owe money

  balances.forEach(b => {
    const net = parseFloat(b.net_balance) || 0;
    
    if (net > BALANCE_THRESHOLD) {
      // This person is owed money (positive balance)
      creditors.push({
        user_id: b.user_id,
        amount: net
      });
    } else if (net < -BALANCE_THRESHOLD) {
      // This person owes money (negative balance)
      debtors.push({
        user_id: b.user_id,
        amount: Math.abs(net)
      });
    }
    // Users with ~0 balance are settled, skip them
  });

  // Sort by amount descending (largest first for greedy algorithm)
  const sortByAmountDesc = (a, b) => b.amount - a.amount;
  creditors.sort(sortByAmountDesc);
  debtors.sort(sortByAmountDesc);

  // Calculate simplified debts using greedy algorithm
  const debts = [];

  while (creditors.length > 0 && debtors.length > 0) {
    const creditor = creditors[0];
    const debtor = debtors[0];

    // Transfer the minimum of what debtor owes and what creditor is owed
    const transferAmount = Math.min(creditor.amount, debtor.amount);

    if (transferAmount > BALANCE_THRESHOLD) {
      debts.push({
        from_user_id: debtor.user_id,
        from_user_name: userInfo[debtor.user_id].name,
        to_user_id: creditor.user_id,
        to_user_name: userInfo[creditor.user_id].name,
        amount: roundToTwoDecimals(transferAmount)
      });
    }

    // Update remaining amounts
    creditor.amount -= transferAmount;
    debtor.amount -= transferAmount;

    // Remove users who are now settled
    if (creditor.amount < BALANCE_THRESHOLD) {
      creditors.shift();
    }
    if (debtor.amount < BALANCE_THRESHOLD) {
      debtors.shift();
    }

    // Re-sort to maintain greedy property
    // (Important when amounts change significantly)
    creditors.sort(sortByAmountDesc);
    debtors.sort(sortByAmountDesc);
  }

  return debts;
}


/**
 * Round a number to two decimal places.
 * Uses Math.round to handle floating point precision issues.
 * 
 * @param {number} num - The number to round
 * @returns {number} The rounded number
 */
function roundToTwoDecimals(num) {
  return Math.round(num * 100) / 100;
}


/**
 * Format a balance amount for display.
 * Shows positive values as "gets back" and negative as "owes".
 * 
 * @param {number} balance - The net balance amount
 * @param {string} currency - Currency code (default: 'EUR')
 * @returns {Object} Formatted balance info
 * 
 * @example
 * formatBalance(50, 'EUR')
 * // Returns: { text: "gets back", amount: "€50.00", isPositive: true }
 * 
 * formatBalance(-30, 'USD')
 * // Returns: { text: "owes", amount: "$30.00", isPositive: false }
 */
export function formatBalance(balance, currency = 'EUR') {
  const isPositive = balance >= 0;
  const absAmount = Math.abs(balance);
  
  // Format currency
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return {
    text: isPositive ? 'gets back' : 'owes',
    amount: formatter.format(absAmount),
    isPositive: isPositive,
    isSettled: absAmount < BALANCE_THRESHOLD
  };
}


/**
 * Calculate the total amount in simplified debts.
 * Useful for showing "X total to settle".
 * 
 * @param {Array} debts - Array of debt objects
 * @returns {number} Total amount across all debts
 */
export function getTotalDebtAmount(debts) {
  if (!debts || !Array.isArray(debts)) return 0;
  return debts.reduce((sum, debt) => sum + (debt.amount || 0), 0);
}


/**
 * Get debts involving a specific user.
 * 
 * @param {Array} debts - Array of debt objects
 * @param {number} userId - The user ID to filter by
 * @returns {Object} Object with 'owes' and 'owed' arrays
 */
export function getUserDebts(debts, userId) {
  if (!debts || !Array.isArray(debts)) {
    return { owes: [], owed: [] };
  }
  
  return {
    owes: debts.filter(d => d.from_user_id === userId),
    owed: debts.filter(d => d.to_user_id === userId)
  };
}


/**
 * Check if a group is fully settled (no outstanding debts).
 * 
 * @param {Array} balances - Array of balance objects
 * @returns {boolean} True if all balances are within threshold
 */
export function isGroupSettled(balances) {
  if (!balances || !Array.isArray(balances)) return true;
  
  return balances.every(b => {
    const net = Math.abs(parseFloat(b.net_balance) || 0);
    return net < BALANCE_THRESHOLD;
  });
}


/**
 * Get summary statistics for a group's balances.
 * 
 * @param {Array} balances - Array of balance objects
 * @returns {Object} Summary statistics
 */
export function getBalanceSummary(balances) {
  if (!balances || !Array.isArray(balances) || balances.length === 0) {
    return {
      totalPositive: 0,
      totalNegative: 0,
      memberCount: 0,
      settledCount: 0,
      isSettled: true
    };
  }
  
  let totalPositive = 0;
  let totalNegative = 0;
  let settledCount = 0;
  
  balances.forEach(b => {
    const net = parseFloat(b.net_balance) || 0;
    
    if (Math.abs(net) < BALANCE_THRESHOLD) {
      settledCount++;
    } else if (net > 0) {
      totalPositive += net;
    } else {
      totalNegative += Math.abs(net);
    }
  });
  
  return {
    totalPositive: roundToTwoDecimals(totalPositive),
    totalNegative: roundToTwoDecimals(totalNegative),
    memberCount: balances.length,
    settledCount: settledCount,
    isSettled: settledCount === balances.length
  };
}


// ===============================================================================
// DEFAULT EXPORT
// ===============================================================================

const balanceUtils = {
  simplifyDebts,
  formatBalance,
  getTotalDebtAmount,
  getUserDebts,
  isGroupSettled,
  getBalanceSummary,
  BALANCE_THRESHOLD
};

export default balanceUtils;

